import { useState, useEffect } from "react";
import axios from "axios";

export default function ClientOnboarding() {
    const [step, setStep] = useState(1);
    const parentClientId = localStorage.getItem("parentClientId");
    const [expandedPickups, setExpandedPickups] = useState({});
    const [addingNewPickup, setAddingNewPickup] = useState(false);
    const [uploading, setUploading] = useState(false);

    const [formData, setFormData] = useState({
        parent_client_id: parentClientId,
        basic_details: { email: "", phone: "", name: "" },
        bank_details: { account_number: "", ifsc_code: "", bank_name: "" },
        address_details: { line1: "", city: "", state: "", country: "", pincode: "" },
        pickup_details: [],
        wallet_type_preference: "PREPAID",
    });

    const [uploadedDocuments, setUploadedDocuments] = useState({
        gst: false,
        pan: false,
        registration: false,
    });

    const [selectedFiles, setSelectedFiles] = useState({
        gst: null,
        pan: null,
        registration: null,
    });

    const [newPickup, setNewPickup] = useState({
        pickup_poc: "",
        pickup_organisation: "",
        pickup_address: "",
        pickup_city: "",
        pickup_state: "",
        pickup_country_code: "IN",
        pickup_postal_code: "",
        pickup_poc_email: "",
        pickup_poc_contact_number: "",
        pickup_hub_code: "",
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            parent_client_id: parentClientId ?? prev.parent_client_id,
        }));
    }, [parentClientId]);

    const handleChange = (e, section, field) => {
        const value = e.target.value;
        setFormData((prev) => ({
            ...prev,
            [section]: { ...(prev[section] || {}), [field]: value }, 
        }));        

        validateField(section, field, value);
    };

    const validateField = (section, field, value) => {
        let errorMsg = "";

        if (section === "basicDetails") {
            if (field === "email" && !/^\S+@\S+\.\S+$/.test(value)) errorMsg = "Invalid email format";
            if (field === "phone" && !/^\d{10}$/.test(value)) errorMsg = "Phone number must be 10 digits";
            if (field === "name" && value.trim() === "") errorMsg = "Name is required";
        }
        if (section === "bankDetails") {
            if (field === "accountNumber" && value.trim() === "") errorMsg = "Account number is required";
            if (field === "ifscCode" && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value)) errorMsg = "Invalid IFSC code";
            if (field === "bankName" && value.trim() === "") errorMsg = "Bank name is required";
        }
        if (section === "addressDetails") {
            if (["line1", "city", "state", "country"].includes(field) && value.trim() === "") {
                errorMsg = `${field.replace(/([A-Z])/g, " $1")} is required`;
            }
            if (field === "pincode" && !/^\d{6}$/.test(value)) errorMsg = "Pincode must be 6 digits";
        }

        setErrors((prev) => ({ ...prev, [field]: errorMsg }));
        return !errorMsg;
    };

    const handleFileSelection = (e, documentType) => {
        const file = e.target.files[0];

        if (file && file.type !== "application/pdf") {
            setErrors((prev) => ({ ...prev, [documentType]: "Only PDF files are allowed!" }));
            return;
        }

        setSelectedFiles((prev) => ({
            ...prev,
            [documentType]: file,
        }));

        setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[documentType];
            return newErrors;
        });
    };

    const handleUploadDocument = async (documentType) => {
        const file = selectedFiles[documentType];

        if (!file) {
            alert(`Please select a ${documentType.toUpperCase()} document before updating.`);
            return;
        }

        setUploading(true);
        const formDataObj = new FormData();
        formDataObj.append("file", file);

        try {
            const authToken = localStorage.getItem("authToken");
            const apiUrl = `${process.env.REACT_APP_POCAS_BASE_URL}/v1/pocas/onboarding/${parentClientId}/upload/${documentType}`;
            await axios.post(apiUrl, formDataObj, {
                headers: { "Content-Type": "multipart/form-data", "Authorization": authToken },
            });

            setUploadedDocuments((prev) => ({
                ...prev,
                [documentType]: true,
            }));
        } catch (error) {
            alert(`Failed to upload ${documentType.toUpperCase()} document.`);
        } finally {
            setUploading(false);
        }
    };

    const handleAddPickup = () => {
        if (Object.values(newPickup).some((value) => !value.trim())) {
            alert("Please fill all fields before adding a new pickup.");
            return;
        }

        setFormData((prev) => ({
            ...prev,
            pickupDetails: [...prev.pickupDetails, newPickup],
        }));

        setExpandedPickups((prev) => ({
            ...prev,
            [formData.pickupDetails.length]: true,
        }));

        setNewPickup({
            pickupPoc: "",
            pickupOrganisation: "",
            pickupAddress: "",
            pickupCity: "",
            pickupState: "",
            pickupCountryCode: "IN",
            pickupPostalCode: "",
            pickupPocEmail: "",
            pickupPocContactNumber: "",
            pickupHubCode: "",
        });

        setAddingNewPickup(false);
    };

    const handleCancelNewPickup = () => {
        setNewPickup({
            pickupPoc: "",
            pickupOrganisation: "",
            pickupAddress: "",
            pickupCity: "",
            pickupState: "",
            pickupCountryCode: "IN",
            pickupPostalCode: "",
            pickupPocEmail: "",
            pickupPocContactNumber: "",
            pickupHubCode: "",
        });

        setAddingNewPickup(false);
    };

    const handleDeletePickup = (index) => {
        setFormData((prev) => ({
            ...prev,
            pickupDetails: prev.pickupDetails.filter((_, i) => i !== index),
        }));

        setExpandedPickups((prev) => {
            const newExp = { ...prev };
            delete newExp[index]; // Remove toggle state
            return newExp;
        });
    };

    const togglePickup = (index) => {
        setExpandedPickups((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    const validateStep = () => {
        const newErrors = {};

        if (step === 1) {
            ["email", "phone", "name"].forEach((field) => {
                if (!validateField("basic_details", field, formData.basic_details[field])) newErrors[field] = "Required";
            });
        }
        if (step === 2) {
            ["account_number", "ifsc_code", "bank_name"].forEach((field) => {
                if (!validateField("bank_details", field, formData.bank_details?.[field] || "")) {
                    newErrors[field] = "Required";
                }
            });            
        }
        if (step === 3) {
            ["line1", "city", "state", "country", "pincode"].forEach((field) => {
                if (!validateField("address_details", field, formData.address_details?.[field] || "")) {
                    newErrors[field] = "Required";
                }
            });
            
        }
        if (step === 4) {
            if (!uploadedDocuments.gst) newErrors.gst = "GST document must be uploaded.";
            if (!uploadedDocuments.pan) newErrors.pan = "PAN card must be uploaded.";
            if (selectedFiles.registration && !uploadedDocuments.registration) {
                newErrors.registration = "If selected, the Registration document must be uploaded.";
            }
        }
        if (step === 5 && formData.pickup_details.length === 0) {
            newErrors.pickup_details = "At least one pickup location is required.";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    const handleNext = () => {
        if (validateStep()) {
            setErrors({});
            setStep((prev) => prev + 1);
        }
    };

    const handleBack = () => setStep((prev) => prev - 1);

    const handleSubmit = async () => {
        if (validateStep()) {
            const submitOnboardingUrl =  `${process.env.REACT_APP_POCAS_BASE_URL}${process.env.REACT_APP_SUBMIT_ONBOARDING}`;
            try {
                const { documents, ...submitData } = formData;
                await axios.post(submitOnboardingUrl, submitData);
                alert("Onboarding initiated successfully!");
            } catch (error) {
                alert("Error initiating onboarding.");
            }
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-20 bg-white shadow-2xl rounded-2xl transition-all duration-300">
            {/* Header */}
            <h2 className="text-4xl font-extrabold text-gray-800 mb-8 text-center tracking-wide">Client Onboarding</h2>

            {/* Step Progress Bar */}
            <div className="flex justify-between mb-8">
                {["Basic Details", "Bank Details", "Address", "Documents", "Pickup Details", "Wallet"].map((tab, index) => (
                    <div key={index} className="flex flex-col items-center">
                        <div className={`h-12 w-12 flex items-center justify-center rounded-full text-lg font-semibold transition-all duration-300
                            ${step > index + 1 ? "bg-green-500 text-white shadow-lg" : step === index + 1 ? "bg-blue-600 text-white shadow-md" : "bg-gray-300 text-gray-600"}`}>
                            {index + 1}
                        </div>
                        <p className={`mt-2 text-sm ${step === index + 1 ? "text-blue-600 font-semibold" : "text-gray-500"}`}>{tab}</p>
                    </div>
                ))}
            </div>

            {/* Step 1: Basic Details */}
            {step === 1 && (
                <>
                    <h3 className="text-2xl font-semibold mb-6 text-gray-800">Step 1: Basic Details</h3>
                    <div className="grid grid-cols-2 gap-6">
                        {["email", "phone", "name"].map((field) => (
                            <div key={field} className="relative">
                                <label className="block text-gray-700 capitalize mb-1 font-medium">{field}</label>
                                <input
                                    type={field === "email" ? "email" : "text"}
                                    value={formData.basic_details?.[field] || ""}
                                    onChange={(e) => handleChange(e, "basic_details", field)}
                                    className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all duration-300"
                                />
                                {errors[field] && <p className="absolute text-red-600 text-xs mt-1">{errors[field]}</p>}
                            </div>
                        ))}

                    </div>
                </>
            )}

            {/* Step 2: Bank Details */}
            {step === 2 && (
                <>
                    <h3 className="text-2xl font-semibold mb-6 text-gray-800">Step 2: Bank Details</h3>
                    <div className="grid grid-cols-2 gap-6">
                        {["accountNumber", "ifscCode", "bankName"].map((field) => (
                            <div key={field} className="relative">
                                <label className="block text-gray-700 capitalize mb-1 font-medium">{field.replace(/([A-Z])/g, " $1")}</label>
                                <input
                                    type="text"
                                    value={formData.bank_details[field]}
                                    onChange={(e) => handleChange(e, "bankDetails", field)}
                                    className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all duration-300"
                                />
                                {errors[field] && <p className="absolute text-red-600 text-xs mt-1">{errors[field]}</p>}
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* Step 3: Address */}
            {step === 3 && (
                <>
                    <h3 className="text-2xl font-semibold mb-6 text-gray-800">Step 3: Address</h3>
                    <div className="grid grid-cols-2 gap-6">
                        {["line1", "city", "state", "country", "pincode"].map((field) => (
                            <div key={field} className="relative">
                                <label className="block text-gray-700 capitalize mb-1 font-medium">{field}</label>
                                <input
                                    type="text"
                                    value={formData.address_details[field]}
                                    onChange={(e) => handleChange(e, "addressDetails", field)}
                                    className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all duration-300"
                                />
                                {errors[field] && <p className="absolute text-red-600 text-xs mt-1">{errors[field]}</p>}
                            </div>
                        ))}
                    </div>
                </>
            )}
            {step === 4 && (
                <>
                    <h3 className="text-2xl font-semibold mb-6 text-gray-800">Step 4: Upload Documents</h3>
                    <div className="grid grid-cols-2 gap-6">
                        {["gst", "pan", "registration"].map((docType) => (
                            <div key={docType} className="relative border border-gray-300 p-4 rounded-lg">
                                <label className="block text-gray-700 capitalize mb-1 font-medium">
                                    {docType.toUpperCase()} Document {docType === "registration" && "(Optional)"}
                                </label>

                                <input
                                    type="file"
                                    accept="application/pdf"
                                    onChange={(e) => handleFileSelection(e, docType)}
                                    className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all duration-300"
                                />

                                {selectedFiles[docType] && (
                                    <p className="mt-2 text-sm text-green-700">
                                        Selected: {selectedFiles[docType].name}
                                    </p>
                                )}

                                <button
                                    onClick={() => handleUploadDocument(docType)}
                                    disabled={uploading || uploadedDocuments[docType]}
                                    className={`mt-3 px-4 py-2 rounded-lg text-white ${uploadedDocuments[docType] ? "bg-gray-400" : "bg-blue-500 hover:bg-blue-600"}`}
                                >
                                    {uploadedDocuments[docType] ? "Uploaded" : "Update"}
                                </button>

                                {errors[docType] && <p className="text-red-600 text-xs mt-1">{errors[docType]}</p>}
                            </div>
                        ))}
                    </div>
                </>
            )}

            {step === 5 && (
                <>
                    <h3 className="text-2xl font-semibold mb-6 text-gray-800">Step 5: Pickup Details</h3>

                    {formData.pickup_details.map((pickup, index) => (
                        <div key={index} className="mb-4 border border-gray-300 rounded-lg p-4">
                            <button
                                onClick={() => togglePickup(index)}
                                className="w-full flex justify-between items-center text-gray-800 font-semibold text-lg focus:outline-none"
                            >
                                Pickup {index + 1}
                                <span className="text-blue-500">{expandedPickups[index] ? "▲" : "▼"}</span>
                            </button>

                            {expandedPickups[index] && (
                                <div className="grid grid-cols-2 gap-4 mt-4">
                                    {Object.keys(pickup).map((field) => (
                                        <div key={field}>
                                            <label className="block text-gray-700 font-medium capitalize">
                                                {field.replace(/([A-Z])/g, " $1")}
                                            </label>
                                            <input
                                                type="text"
                                                value={pickup[field]}
                                                readOnly
                                                className="w-full border p-3 rounded-lg bg-gray-100"
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}

                            <button
                                onClick={() => handleDeletePickup(index)}
                                className="mt-4 bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-lg"
                            >
                                Delete
                            </button>
                        </div>
                    ))}

                    {addingNewPickup ? (
                        <div className="border border-gray-300 rounded-lg p-4">
                            <h3 className="text-xl font-semibold mb-4 text-gray-800">New Pickup</h3>
                            <div className="grid grid-cols-2 gap-6">
                                {Object.keys(newPickup).map((field) => (
                                    <div key={field}>
                                        <label className="block text-gray-700 font-medium capitalize">
                                            {field.replace(/([A-Z])/g, " $1")}
                                        </label>
                                        <input
                                            type="text"
                                            value={newPickup[field]}
                                            onChange={(e) => setNewPickup({ ...newPickup, [field]: e.target.value })}
                                            className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-blue-400 transition-all duration-300"
                                        />
                                    </div>
                                ))}
                            </div>

                            <div className="flex justify-between mt-4">
                                <button onClick={handleAddPickup} className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg">
                                    Save Pickup
                                </button>
                                <button onClick={handleCancelNewPickup} className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-lg">
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            onClick={() => setAddingNewPickup(true)}
                            className="mt-6 bg-green-400 hover:bg-green-500 text-white px-5 py-3 rounded-lg w-full transition-all duration-300"
                        >
                            + Add Pickup
                        </button>
                    )}
                </>
            )}

            {/* Step 6: Wallet Preferences */}
            {step === 6 && (
                <>
                    <h3 className="text-2xl font-semibold mb-6 text-gray-800">Step 6: Wallet Preferences</h3>
                    <div className="grid grid-cols-1 gap-6">
                        <div className="relative">
                            <label className="block text-gray-700 capitalize mb-1 font-medium">Select Wallet Type</label>
                            <select
                                value={formData.wallet_type_preference}  // ✅ Fix applied
                                onChange={(e) => setFormData({ ...formData, walletTypePreference: e.target.value })}  // ✅ Directly updating
                                className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all duration-300"
                            >
                                <option value="PREPAID">Prepaid</option>
                                <option value="POSTPAID">Postpaid</option>
                            </select>
                        </div>
                    </div>
                </>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
                {step > 1 && (
                    <button onClick={handleBack} className="bg-gray-400 hover:bg-gray-500 text-white px-6 py-3 rounded-lg transition-all duration-300 shadow-md">
                        Back
                    </button>
                )}

                <button
                    onClick={step < 6 ? handleNext : handleSubmit}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-all duration-300 shadow-md font-semibold"
                >
                    {step < 6 ? "Next" : "Submit"}
                </button>
            </div>
        </div>
    );

}